import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DCarbonizeAppConfig } from 'app/@types/shared/app-config';
import { ConfigService } from 'app/app-config.service';
import { AnalyticsService } from './analytics.service';
import { filter, map } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { OrganizationState } from '@state/organization.state';
import { UserState } from '@state/user.state';

/**
 * Global function to push events to the GTM dataLayer
 * @param args - Variable number of arguments to be pushed to the dataLayer
 */
function gtag(...args: any) {
    (<any>window).dataLayer.push(...args);
}

/**
 * Service responsible for managing Google Tag Manager integration
 * Handles dynamic script injection, page view tracking, and custom event tracking
 * with additional context like license level and traffic type
 */
@Injectable({
    providedIn: 'root',
})
export class GoogleTagManagerService extends AnalyticsService {
    private config: DCarbonizeAppConfig = this.configService.getConfig;
    private isInitialized = false;

    constructor(private configService: ConfigService, private router: Router, private store: Store) {
        super();
    }

    /**
     * Determines if the traffic is internal based on the user's email domain
     * @param email - User's email address
     * @returns boolean indicating if the traffic is internal
     */
    private isInternalTraffic(email: string): boolean {
        return email.endsWith('@d-carbonize.eu') || email.endsWith('@yopmail.com');
    }

    /**
     * Initializes Google Tag Manager and sets up page view tracking
     * This method:
     * 1. Checks if GTM is configured
     * 2. Initializes the dataLayer
     * 3. Injects the GTM script
     * 4. Sets up the noscript iframe
     * 5. Configures page view tracking with additional context
     */
    public activate() {
        // Check if GTM is already initialized
        if (this.isInitialized) {
            console.warn('Google Tag Manager is already initialized');
            return;
        }

        // Check if GTM is configured
        if (!this.config.googleTagManager.id) {
            console.warn('Google Tag Manager ID is not configured');
            return;
        }

        // Initialize dataLayer if it doesn't exist
        if (!(<any>window).dataLayer) {
            (<any>window).dataLayer = [];
        }

        // Inject the main GTM script
        const script = document.createElement('script');
        script.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${this.config.googleTagManager.id}');
        `;
        document.head.appendChild(script);

        // Create and inject the noscript iframe for browsers with JavaScript disabled
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.config.googleTagManager.id}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);

        // Set up page view tracking with additional context
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => event as NavigationEnd),
            )
            .subscribe((event) => {
                const context = {
                    page_path: event.urlAfterRedirects,
                    page_title: document.title,
                };
                // Get the current license level
                const pricingPlan = this.store.selectSnapshot(OrganizationState.getPricingPlan);
                const licenseLevel = pricingPlan?.name;
                if (licenseLevel) {
                    Object.assign(context, { license_level: licenseLevel });
                }

                // Get the current user's email to determine traffic type
                const currentUser = this.store.selectSnapshot(UserState.getCurrentUser);
                const email = currentUser?.email || '';
                const trafficType = this.isInternalTraffic(email) ? 'internal' : 'external';
                if (trafficType === 'internal') {
                    Object.assign(context, { traffic_type: trafficType });
                }

                // Push the page view event to GTM with all context
                gtag('event', 'page_view', context);
            });

        // Mark GTM as initialized
        this.isInitialized = true;
    }
}
